import { Button, Checkbox, Label, Row, Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import ConditionalContainer from 'components/foundation/ConditionalContainer/ConditionalContainer';
import useSlackDocumentsAccess from 'containers/settings/integrations/vendors/slack/hooks/useSlackDocumentsAccess';
import { useToastDispatch } from 'contexts/ToastContext';
import useOrganizationUiSettingsUpdate from 'hooks/models/organization/settings/hooks/useOrganizationUiSettingsUpdate';
import { FunctionComponent, useState } from 'react';
import { t } from 'utils/localize';
import './DocumentEnableForm.scss';

export interface ICreateDocumentDialogProps {
  onRequestSubmitted: () => void;
  className?: string | object;
}

export const DocumentEnableForm: FunctionComponent<ICreateDocumentDialogProps> = (props) => {
  const { className, onRequestSubmitted } = props;
  const toastDispatch = useToastDispatch();
  const [agreeToCharge, setAgreeToCharge] = useState(false);
  const [showErrorTip, setShowErrorTip] = useState(false);
  const documentsAccess = useSlackDocumentsAccess();
  const updateUiSettings = useOrganizationUiSettingsUpdate();

  const classes = classNames('document-enable-form', className);

  const onLinkClicked = () => {
    window.open('https://busybusy.com/product-videos/?wchannelid=q3wum81pn9&wmediaid=wniq7qdmmh');
  };

  const requestAccess = async () => {
    let requestSuccessful = false;

    try {
      const response = await documentsAccess();
      requestSuccessful = response.status === 200;
    } catch (e) {
      requestSuccessful = false;
    }

    if (requestSuccessful) {
      updateUiSettings('requestedDocumentsAccess', true);
      onRequestSubmitted();
    } else {
      toastDispatch({
        type: 'OPEN',
        payload: { label: t('Failed to send request. Try again later.'), theme: Theme.DANGER },
      });
      setShowErrorTip(true);
    }
  };

  const customerSupport = () => {
    (window as any).Intercom('show');
  };

  return (
    <div className={classes}>
      <span className="content">
        {t('Upload, share, and organize your project documents, all within the busybusy app. Want to learn more?')}
        <a className="link ml-1" onClick={() => onLinkClicked()}>
          {t('Click here.')}
        </a>
      </span>

      <Label className="mt-7 mb-2">{t('Getting Started')}</Label>
      {t(
        'Simply agree to the additional charge, then press on the Request Access button below. The activation process usually takes less than 24 hours. We will send you a message to notify you when the feature has been enabled.'
      )}

      <Label className="mt-7 mb-2">{t('Pricing')}</Label>
      {t('$2 per active busybusy user per month')}

      <Checkbox
        className="mt-6"
        type="basic"
        label={t(
          `I understand that by enabling the documents feature, an additional charge of $2 per employee will be charged to my card each month.`
        )}
        checked={agreeToCharge}
        onChange={setAgreeToCharge}
      />

      <Row className="mt-9">
        <Button type="primary" onClick={requestAccess} disabled={!agreeToCharge}>
          {t('Request Access')}
        </Button>

        <Button className="ml-3" type="secondary" onClick={customerSupport}>
          {t('Customer Support')}
        </Button>
      </Row>

      <ConditionalContainer condition={showErrorTip}>
        <br />
        {t('* If you have an ad blocker, disable it and try again.')}
      </ConditionalContainer>
    </div>
  );
};

export default DocumentEnableForm;
